import { getValue } from 'firebase/remote-config';
import { IUnlikelyShiftConfig } from '~/models/remoteConfig';
import { remoteConfig } from '../api/firebaseConfig';

/**
 * Keys for Firebase Remote Config keys.
 * Can be used when calling {@link remoteConfig}'s `getValue()`
 *
 * @enum {string}
 */
const RemoteConfigKeys = {
  allowedWcStates: 'ALLOWED_WC_STATES',
  offerExpirationStates: 'OFFER_EXPIRATION_STATES',
  unlimitedApplicationEnabledStates: 'UNLIMITED_APPLICATION_ENABLED_STATES',
  statesToDisableTempRequests: 'BLOCKED_STATES',
  autofillMinAttendanceScore: 'AUTOFILL_MIN_ATTENDANCE_SCORE',
  maxRepostTimeThreshold: 'MAX_REPOST_TIME_THRESHOLD',
  unlikelyShiftConfiguration: 'UNLIKELY_SHIFT_CONFIG',
  bestInDentalNomineePopup: 'BEST_IN_DENTAL_OFFICE_NOMINEE_SURVEY_LINK',
  officeTutorials: 'OFFICE_TUTORIAL_CONFIG',
  apiDefaultTimeoutMs: 'API_DEFAULT_TIMEOUT_MS',
  apiMaxRetryAttempts: 'API_MAX_RETRY_ATTEMPTS',
  apiRetryWhitelistedUrls: 'API_RETRY_WHITELISTED_URLS',
  referralLinkMessages: 'REFERRAL_LINK_MESSAGES',
  chargeTimelineDays: 'CHARGE_TIMELINE_DAYS',
  closeJobFeedbackPositive: 'CLOSE_JOB_FEEDBACK_POSITIVE',
  closeJobFeedbackNegative: 'CLOSE_JOB_FEEDBACK_NEGATIVE',
};

export const RemoteConfigUtils = {
  getAllowedWcStates() {
    return getValue(remoteConfig, RemoteConfigKeys.allowedWcStates).asString();
  },

  getOfferExpirationStates() {
    return getValue(remoteConfig, RemoteConfigKeys.offerExpirationStates).asString();
  },

  getUnlimitedApplicationEnabledStates() {
    return getValue(remoteConfig, RemoteConfigKeys.unlimitedApplicationEnabledStates)
      ?.asString()
      ?.replace(/\s+/g, '')
      .split(',');
  },

  getDisabledTempPostStates() {
    const stateToDisableTempJobPost = getValue(
      remoteConfig,
      RemoteConfigKeys.statesToDisableTempRequests,
    )
      ?.asString()
      ?.replace(/\s+/g, '');

    return stateToDisableTempJobPost?.split(',');
  },

  getAutofillMinAttendanceScore() {
    return getValue(remoteConfig, RemoteConfigKeys.autofillMinAttendanceScore).asString();
  },

  getMaxRepostTimeThreshold() {
    return getValue(remoteConfig, RemoteConfigKeys.maxRepostTimeThreshold).asNumber();
  },

  getUnlikelyShiftConfiguration() {
    try {
      const unlikelyShiftConfiguration = getValue(
        remoteConfig,
        RemoteConfigKeys.unlikelyShiftConfiguration,
      ).asString();

      return unlikelyShiftConfiguration
        ? (JSON.parse(unlikelyShiftConfiguration) as IUnlikelyShiftConfig)
        : null;
    } catch {
      return null;
    }
  },

  getBestInDentalOfficeNomineeSurvey() {
    return getValue(remoteConfig, RemoteConfigKeys.bestInDentalNomineePopup).asString();
  },

  getOfficeTutorials() {
    try {
      const tutorials = getValue(remoteConfig, RemoteConfigKeys.officeTutorials);

      if (tutorials) {
        return JSON.parse(tutorials.asString());
      }

      return [];
    } catch {
      return [];
    }
  },

  getApiDefaultTimeoutMs(): number {
    return getValue(remoteConfig, RemoteConfigKeys.apiDefaultTimeoutMs).asNumber();
  },

  getApiMaxRetryAttempts(): number {
    return getValue(remoteConfig, RemoteConfigKeys.apiMaxRetryAttempts).asNumber();
  },

  getApiRetryWhitelistedUrls(): string[] {
    try {
      const urls = getValue(remoteConfig, RemoteConfigKeys.apiRetryWhitelistedUrls);

      if (urls) {
        return JSON.parse(urls.asString());
      }

      return [];
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      return [];
    }
  },

  getReferralLinkMessages() {
    try {
      const referralLinkMessages = getValue(remoteConfig, RemoteConfigKeys.referralLinkMessages);

      if (referralLinkMessages) {
        return JSON.parse(referralLinkMessages.asString());
      }

      return [];
    } catch {
      return [];
    }
  },

  getChargeTimelineDays(): number {
    return getValue(remoteConfig, RemoteConfigKeys.chargeTimelineDays).asNumber();
  },

  getCloseJobFeedbackPositive(): string[] {
    try {
      const positive = getValue(remoteConfig, RemoteConfigKeys.closeJobFeedbackPositive);

      if (positive) {
        return JSON.parse(positive.asString());
      }

      return [];
    } catch {
      return [];
    }
  },

  getCloseJobFeedbackNegative(): string[] {
    try {
      const negative = getValue(remoteConfig, RemoteConfigKeys.closeJobFeedbackNegative);

      if (negative) {
        return JSON.parse(negative.asString());
      }

      return [];
    } catch {
      return [];
    }
  },
};
